import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { useAuth } from './useAuth';
import { isKeycloakConfigured } from './keycloak';

interface OrgGuardProps {
  children: React.ReactNode;
}

const IS_DEV = import.meta.env.DEV;

export function OrgGuard({ children }: OrgGuardProps) {
  const { isAuthenticated, isLoading, user, login } = useAuth();
  const loginCalledRef = useRef(false);

  useEffect(() => {
    // Only trigger login redirect once — prevents infinite loops.
    // If not loading, not authenticated, and Keycloak is configured:
    if (!isLoading && !isAuthenticated && isKeycloakConfigured() && !loginCalledRef.current) {
      loginCalledRef.current = true;
      // Small delay so Keycloak has fully settled before redirecting
      const timer = setTimeout(() => {
        login();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isAuthenticated, login]);

  // Still loading KC or Supabase org hydration hasn't finished
  if (isLoading || (isAuthenticated && user === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Connecting...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Keycloak not configured — show dev setup notice
    if (IS_DEV && !isKeycloakConfigured()) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-950 p-4">
          <div className="max-w-sm w-full bg-gray-900 border border-gray-800 rounded-xl p-6 text-center">
            <div className="w-10 h-10 bg-brand-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-white font-semibold mb-2">GoGrabJob Business</h2>
            <p className="text-gray-400 text-sm mb-4">
              Keycloak is not configured for local development.
            </p>
            <div className="text-left text-xs bg-gray-800 rounded-lg p-3 space-y-1 mb-4">
              <p className="text-gray-300 font-mono">Set in <span className="text-brand-400">B2B/.env</span>:</p>
              <p className="text-gray-400 font-mono">VITE_KEYCLOAK_URL=http://localhost:8180</p>
              <p className="text-gray-400 font-mono">VITE_KEYCLOAK_REALM=gograbjob</p>
              <p className="text-gray-400 font-mono">VITE_KEYCLOAK_CLIENT_ID=b2b-app</p>
            </div>
            <button onClick={login} className="btn-primary w-full mt-4">
              Try Login Anyway
            </button>
          </div>
        </div>
      );
    }

    // Configured — redirect is handled by useEffect above.
    // Show spinner while the redirect is in flight.
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  if (!user?.orgId) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
